<template>
  <div class="process-content">
    <el-timeline style="max-width: 100%; margin: 0 auto; padding: 0 10px">
      <el-timeline-item
        v-for="(item, index) in processList"
        :key="item.id"
        :timestamp="item.name"
        placement="top"
        :type="getStatusType(item)"
      >
        <el-card class="timeline-card" :class="getStatusClass(item)">
          <h3 class="card-title">{{ index + 1 + "、" + item.name }}</h3>
          <div class="card-content" v-html="formatContent(item.content)"></div>
          <div class="card-date">
            <el-tag :type="getStatusType(item)" size="small">
              时间: {{ formatDateRange(item.starttime, item.endtime) }}
            </el-tag>
          </div>
        </el-card>
      </el-timeline-item>
    </el-timeline>
  </div>
</template>

<script setup>
import { onMounted, ref, onUnmounted } from "vue";
import { getProcessList } from "@/api/processApi";

const processList = ref([]);
const currentTime = ref(Date.now());

const fetchProcessData = async () => {
  try {
    const response = await getProcessList();
    if (response.data.code === 200) {
      processList.value = response.data.data;
    }
  } catch (error) {
    console.error("请求失败:", error);
  }
};

let timeInterval;
onMounted(() => {
  fetchProcessData();
  setInterval(fetchProcessData, 60 * 60 * 1000);

  timeInterval = setInterval(() => {
    currentTime.value = Date.now();
  }, 60 * 1000);
});

onUnmounted(() => clearInterval(timeInterval));

const parseCustomDate = (dateStr) => {
  if (!dateStr) return new Date();

  const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return new Date(
      parseInt(isoMatch[1]),
      parseInt(isoMatch[2]) - 1,
      parseInt(isoMatch[3])
    );
  }

  const cnMatch = dateStr.match(/^(\d{1,2})月(\d{1,2})日?$/);
  if (cnMatch) {
    const now = new Date();
    const year = now.getFullYear();
    const month = parseInt(cnMatch[1]) - 1;
    const day = parseInt(cnMatch[2]);
    const date = new Date(year, month, day);
    return date < now ? new Date(year + 1, month, day) : date;
  }

  const date = new Date(dateStr);
  return isNaN(date) ? new Date() : date;
};

const getStatus = (item) => {
  try {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const start = parseCustomDate(item.starttime);
    start.setHours(0, 0, 0, 0);

    const end = parseCustomDate(item.endtime);
    end.setHours(0, 0, 0, 0);

    if (now < start) return "future";
    if (now > end) return "past";
    return "current";
  } catch (error) {
    console.error("Error determining status:", error);
    return "past";
  }
};

const getStatusType = (item) => {
  const status = getStatus(item);
  return status === "past" ? "info" : status === "current" ? "primary" : "";
};

const getStatusClass = (item) => {
  return getStatus(item);
};

const formatDateRange = (start, end) => {
  return `${start} - ${end}`;
};

const formatContent = (content) => {
  if (!content) return "";
  return content;
};
</script>

<style scoped>
.process-content {
  padding: 5px 0;
}

.timeline-card {
  padding: 12px 15px;
  transition: all 0.25s ease;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
}

.timeline-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  transition: all 0.25s ease;
}

.timeline-card.past {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.timeline-card.past::before {
  background-color: #cbd5e1;
}

.timeline-card.past .card-title {
  color: #94a3b8;
}

.timeline-card.past .card-content {
  color: #94a3b8;
}

.timeline-card.current {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  box-shadow: 0 3px 8px rgba(34, 197, 94, 0.2);
  z-index: 2;
}

.timeline-card.current::before {
  background-color: #22c55e;
  width: 4px;
}

.timeline-card.current .card-title {
  color: #16a34a;
  font-weight: 600;
}

.timeline-card.current .el-tag {
  background-color: #22c55e;
  color: white;
}

.timeline-card.current:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.25);
}

.timeline-card.future {
  background: linear-gradient(135deg, #f0fdf4 0%, #f7fef9 100%);
  border: 1px solid #dcfce7;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.1);
}

.timeline-card.future::before {
  background-color: #86efac;
}

.timeline-card.future .card-title {
  color: #15803d;
}

.timeline-card.future .card-content {
  color: #22c55e;
}

.timeline-card.future .el-tag {
  background-color: #dcfce7;
  color: #16a34a;
}

.card-title {
  font-size: 15px;
  margin-top: 0;
  margin-bottom: 8px;
  position: relative;
  padding-left: 25px;
}

.card-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
}

.card-content {
  line-height: 1.6;
  margin-left: 10px;
  font-size: 13px;
  text-indent: 2em;
}

.card-content p {
  margin: 5px 0;
}

.card-date {
  margin-top: 8px;
  text-align: right;
}
</style>
